import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollText, Clock, User, Search, Download, Filter, X } from "lucide-react";
import { format } from "date-fns";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";
import { Pagination, paginate } from "@/components/Pagination";

function inferRelated(log) {
  const d = (log.details || "").toLowerCase();
  const a = log.action || "";
  if (a.includes("fund")) return "Fund";
  if (a.includes("investor") || a.includes("kyc")) return "Investor";
  if (a.includes("admin") || a.includes("user")) return "User";
  if (a.includes("ticket")) return "Support";
  if (a.includes("escalation")) return "Compliance";
  if (d.includes("fund")) return "Fund";
  if (d.includes("investor")) return "Investor";
  return null;
}

const ACTION_COLORS = {
  login: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  create_user: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  update_user: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  deactivate_user: "bg-red-500/10 text-red-400 border-red-500/20",
  reactivate_user: "bg-teal-500/10 text-teal-400 border-teal-500/20",
  change_password: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  reset_password: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  create_fund: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  update_fund: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  delete_fund: "bg-red-500/10 text-red-400 border-red-500/20",
  create_investor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  update_investor: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  upload_document: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  create_capital_txn: "bg-green-500/10 text-green-400 border-green-500/20",
  create_perf_txn: "bg-green-500/10 text-green-400 border-green-500/20",
  update_kyc_status: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  update_platform_config: "bg-pink-500/10 text-pink-400 border-pink-500/20",
  create_escalation: "bg-red-500/10 text-red-400 border-red-500/20",
  resolve_escalation: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [filterAction, setFilterAction] = useState("all");
  const [filterUser, setFilterUser] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAction !== "all") params.action = filterAction;
      if (filterUser) params.user_email = filterUser;
      if (filterSearch) params.search = filterSearch;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const { data } = await API.get("/audit-logs", { params });
      setLogs(data);
    } catch {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterUser, filterSearch, filterDateFrom, filterDateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/audit-logs/actions");
        setActions(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const exportCSV = () => {
    if (logs.length === 0) return;
    const headers = ["Timestamp", "User", "Action", "Details"];
    const rows = logs.map((l) => [
      l.timestamp, l.user_email, l.action, `"${(l.details || "").replace(/"/g, '""')}"`
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported audit logs");
  };

  const clearFilters = () => {
    setFilterAction("all");
    setFilterUser("");
    setFilterSearch("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const hasFilters = filterAction !== "all" || filterUser || filterSearch || filterDateFrom || filterDateTo;

  const { sorted: sortedLogs, sortKey, sortDir, toggleSort } = useSortableTable(logs);
  const { paginatedItems: pagedLogs, totalPages: logsTotalPages, safePage: logsSafePage } = paginate(sortedLogs, page, pageSize);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="audit-title">Audit Logs</h1>
          <p className="text-muted-foreground text-sm mt-1">Complete activity trail across the platform</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={logs.length === 0} className="gap-1.5" data-testid="export-csv-btn">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0 mb-2" />
            <div className="relative flex-1 min-w-[180px] max-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search details..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="audit-search"
              />
            </div>
            <div className="min-w-[160px]">
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="h-9" data-testid="audit-filter-action">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actions.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <Input
                placeholder="Filter by email..."
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="h-9"
                data-testid="audit-filter-user"
              />
            </div>
            <div>
              <Input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="h-9 w-[140px]"
                data-testid="audit-filter-date-from"
              />
            </div>
            <span className="text-xs text-muted-foreground mb-2">to</span>
            <div>
              <Input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="h-9 w-[140px]"
                data-testid="audit-filter-date-to"
              />
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 gap-1 text-xs">
                <X className="w-3 h-3" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <p className="text-xs text-muted-foreground">{logs.length} log entries</p>

      {/* Table */}
      <Card data-testid="audit-logs-table-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <SortableHead sortKey="timestamp" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Timestamp</SortableHead>
                <SortableHead sortKey="user_email" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">User</SortableHead>
                <SortableHead sortKey="action" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Action</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Related To</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold pr-6">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5} className="pl-6"><div className="h-10 bg-secondary/30 rounded animate-pulse" /></TableCell>
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                    <ScrollText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                sortedLogs.length > 0 && pagedLogs.map((log) => {
                  const related = log.related_to;
                  const relatedLabel = related ? `${related.type === "fund_manager" ? "Fund Manager" : related.type?.charAt(0).toUpperCase() + related.type?.slice(1)}${related.name ? `: ${related.name}` : ""}` : inferRelated(log);
                  return (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="pl-6">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Clock className="w-3.5 h-3.5" />
                        {format(new Date(log.timestamp), "MMMM d, yyyy HH:mm:ss")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                        {log.user_email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] font-mono uppercase tracking-wider ${ACTION_COLORS[log.action] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"}`}>
                        {log.action?.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {relatedLabel ? (
                        <Badge variant="secondary" className="text-[10px] font-normal" data-testid={`audit-related-${log.id}`}>
                          {relatedLabel}
                        </Badge>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell className="pr-6 text-sm text-muted-foreground max-w-md truncate">
                      {log.details}
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {!loading && sortedLogs.length > 0 && (
            <div className="px-6 pb-4">
              <Pagination currentPage={logsSafePage} totalPages={logsTotalPages} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
